import type { CatalogImageOwner } from "./catalogQueries";
import type { CatalogDictionaryEntry } from "../products/product3DTypes";

/** Cap so the pinned row stays scannable above the wallpaper browse grid. */
export const USED_WALLPAPERS_MAX = 12;

/**
 * Compact wallpaper SKU already applied somewhere in the open plan.
 * Enough to re-apply via ProductsPanel without a fresh catalog fetch.
 */
export type UsedWallpaperOption = {
  id: string;
  name: string;
  brand?: string;
  imageUrl?: string;
};

/**
 * Builds a minimal catalog owner so “used in plan” wallpapers can re-apply
 * through the same select / preview paths as browse results.
 */
export function usedWallpaperToCatalogOwner(
  wallpaper: UsedWallpaperOption,
): CatalogImageOwner {
  return {
    id: wallpaper.id,
    name: wallpaper.name,
    brand: wallpaper.brand,
    images: wallpaper.imageUrl ? [wallpaper.imageUrl] : [],
  };
}

type SurfaceRefLike = {
  source?: string | null;
  id?: string | null;
  imageUrl?: string | null;
};

export type CollectUsedWallpapersOptions = {
  /** Wall product surface refs in first-sighting order. */
  surfaceRefs: Iterable<SurfaceRefLike | null | undefined>;
  /** Cached product metadata from the floorplan dictionary. */
  dictionary?: Record<string, CatalogDictionaryEntry>;
  max?: number;
};

/**
 * Dedupes in-plan product wallpapers by catalog id (first sighting wins).
 * Non-product surface refs are skipped. Order follows first sighting.
 */
export function collectUsedWallpapers(
  options: CollectUsedWallpapersOptions,
): UsedWallpaperOption[] {
  const {
    surfaceRefs,
    dictionary = {},
    max = USED_WALLPAPERS_MAX,
  } = options;

  const seenIds = new Set<string>();
  const result: UsedWallpaperOption[] = [];

  for (const surfaceRef of surfaceRefs) {
    if (result.length >= max) {
      break;
    }
    if (surfaceRef?.source !== "product") {
      continue;
    }
    const id = surfaceRef.id?.trim();
    if (!id || seenIds.has(id)) {
      continue;
    }
    seenIds.add(id);

    const dictEntry = dictionary[id];
    const imageUrl =
      surfaceRef.imageUrl?.trim() || dictEntry?.imageUrl?.trim() || undefined;
    // Match Product List: never show raw Mongo ids. Cached names equal to the
    // product id (from older getCatalogItemDisplayName fallbacks) also count
    // as missing.
    const cachedName = dictEntry?.name?.trim();
    const name =
      (cachedName && cachedName !== id ? cachedName : undefined) ||
      dictEntry?.brand?.trim() ||
      "Untitled product";
    const brand = dictEntry?.brand?.trim() || undefined;

    result.push({ id, name, brand, imageUrl });
  }

  return result;
}
